import { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { ExpertiseCard } from '../types';

interface ExpertiseSectionProps {
  cards: ExpertiseCard[];
  className?: string;
}

const ExpertiseSection = ({ cards, className = '' }: ExpertiseSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="capabilities"
      ref={sectionRef}
      className={`relative py-20 px-6 ${className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div
          className={`text-center space-y-4 mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient">
            Core Expertise
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Specialized capabilities that set us apart in mission-critical electronics development
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div
              key={card.id}
              className={`glass-effect rounded-lg overflow-hidden transition-all duration-1000 hover:scale-105 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onMouseEnter={() => setHoveredCard(card.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={card.image}
                  alt={card.alt}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={card.icon} size={24} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{card.title}</h3>
                </div>

                <p className="text-muted-foreground leading-relaxed">{card.description}</p>

                <div
                  className={`space-y-2 transition-all duration-500 ${
                    hoveredCard === card.id
                      ? 'max-h-96 opacity-100' :'max-h-0 opacity-0 overflow-hidden'
                  }`}
                >
                  <h4 className="text-sm font-semibold text-foreground">Key Capabilities:</h4>
                  <ul className="space-y-1">
                    {card.capabilities.map((capability, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                        <Icon name="CheckCircle2" size={16} className="text-success mt-0.5 flex-shrink-0" />
                        <span>{capability}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExpertiseSection;